import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

const suggestions = [
  "Storgatan 12, 114 51 Stockholm",
  "Storgatan 5, 411 19 Göteborg",
  "Storsvägen 3, 217 62 Malmö",
];

const tags = [
  "Län", "Kommun", "Stadsdel", "Församling", "LKF-kod",
  "Kommunkod", "Fastighet", "Koordinater", "DeSO", "RegSO",
  "UUID", "Postnummer", "Ort",
];

type Phase = "idle" | "typing" | "dropdown" | "selecting" | "selected" | "validating" | "done";

export function ValidAnimation() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [typedText, setTypedText] = useState("");
  const typingChars = "Stor";

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Start typing at 0.5s
    timers.push(setTimeout(() => setPhase("typing"), 500));

    // Type characters one by one
    for (let i = 0; i < typingChars.length; i++) {
      timers.push(
        setTimeout(() => {
          setTypedText(typingChars.slice(0, i + 1));
        }, 600 + i * 170)
      );
    }

    // Show dropdown at 1.2s
    timers.push(setTimeout(() => setPhase("dropdown"), 1200));

    // Highlight first suggestion at 1.8s
    timers.push(setTimeout(() => setPhase("selecting"), 1800));

    // Select suggestion at 2.0s
    timers.push(setTimeout(() => {
      setPhase("selected");
      setTypedText(suggestions[0]);
    }, 2000));

    // Start validation at 2.3s
    timers.push(setTimeout(() => setPhase("validating"), 2300));

    // Done at 2.5s
    timers.push(setTimeout(() => setPhase("done"), 2500));

    return () => timers.forEach(clearTimeout);
  }, []);

  const showDropdown = phase === "dropdown" || phase === "selecting";
  const showCheck = phase === "validating" || phase === "done";
  const showTags = phase === "done";

  return (
    <div className="relative w-full max-w-lg">
      {/* Address input field */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-5 shadow-2xl relative"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-xs text-white/40 font-body mb-1.5 uppercase tracking-wider">
              Adress
            </div>
            <div className="relative h-7 flex items-center">
              {/* Placeholder */}
              {phase === "idle" && (
                <span className="text-white/30 font-body text-lg whitespace-nowrap">
                  Skriv en adress...
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="inline-block w-0.5 h-5 bg-white/60 align-text-bottom"
                  />
                </span>
              )}

              {/* Typing / selected text */}
              {phase !== "idle" && (
                <span className="text-white font-body text-lg whitespace-nowrap">
                  {typedText}
                  {(phase === "typing" || phase === "dropdown" || phase === "selecting") && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ repeat: Infinity, duration: 0.8 }}
                      className="inline-block w-0.5 h-5 bg-white/60 align-text-bottom"
                      style={{ marginLeft: '1px' }}
                    />
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Checkmark */}
          <AnimatePresence>
            {showCheck && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="w-10 h-10 rounded-full bg-emerald-500/90 flex items-center justify-center flex-shrink-0"
              >
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <motion.path
                    d="M5 13l4 4L19 7"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.4 }}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Status bar */}
        {showCheck && (
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mt-3 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-500/50 rounded-full origin-left"
          />
        )}

        {/* Autocomplete dropdown */}
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 top-full mt-2 bg-white/15 backdrop-blur-md border border-white/20 rounded-lg overflow-hidden shadow-xl z-10"
            >
              {suggestions.map((suggestion, i) => (
                <motion.div
                  key={suggestion}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.06 }}
                  className={`px-4 py-2.5 text-sm font-body cursor-pointer transition-colors ${
                    i === 0 && phase === "selecting"
                      ? "bg-white/20 text-white"
                      : "text-white/70 hover:bg-white/10"
                  }`}
                >
                  {suggestion}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Enrichment tags */}
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {tags.map((tag, i) => (
          <motion.span
            key={tag}
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={showTags ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 10, scale: 0.8 }}
            transition={{ duration: 0.3, delay: showTags ? 0.3 + i * 0.12 : 0 }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-xs text-white/80 font-body"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-coral" />
            {tag}
          </motion.span>
        ))}
      </div>
    </div>
  );
}
